import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';

import { Store } from '@ngrx/store';
import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { BehaviorSubject, combineLatest, filter, map, Observable, Subject, Subscription, switchMap, take } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import {
  CommonBookingState,
  ServicesActions,
  serviceSelectionsForFragment,
  servicesForCategory,
} from '@fcom/common-booking/store';
import { isDeepEqual, isPresent, mapErrorForSentry, SentryLogger, valuesOf } from '@fcom/core/index';
import { AppState } from '@fcom/core/interfaces';
import { Amount, ServiceCatalogServices, TravelerService } from '@fcom/dapi';
import { Category, FinnairItineraryItemFlight, SubCategory } from '@fcom/dapi/api/models';
import { getServiceData } from '@fcom/common-booking/utils/ancillaries.utils';
import { getAllFlightsFromCartOrOrder } from '@fcom/common-booking/utils/common-booking.utils';
import { FragmentService, ToasterService } from '@fcom/common/services';
import { finShare } from '@fcom/rx';
import { CartOrOrder } from '@fcom/common-booking/interfaces';
import { changedServices } from '@fcom/common-booking/modules/ancillaries/utils';
import { GtmEvent, GtmPurchaseFlow } from '@fcom/common/interfaces';
import { GtmService } from '@fcom/common/gtm';
import { ModalButtons } from '@fcom/ui-components';
import { CmsArticle } from '@fcom/core-api/interfaces';

import { BookingQueueService } from '../../../../services';
import {
  PaxServiceCategoryPerFragmentWithSelections,
  ServiceCatalogServiceWithSelection,
  ServiceSelectionChange,
} from '../../../pax-details/interfaces/pax-ancillaries.interface';

@Component({
  selector: 'fin-saf-contribution-product',
  templateUrl: './saf-contribution-product.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SafContributionProductComponent implements OnInit {
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ModalButtons = ModalButtons;

  @Input({ required: true })
  servicesReady$: Observable<boolean>;

  @Input({ required: true })
  cartOrOrder$: Observable<CartOrOrder>;

  safIsLoading$ = new BehaviorSubject<boolean>(false);

  serviceCatalogWithSelection: ServiceCatalogServiceWithSelection;
  travelerId: string;

  safService$: Observable<ServiceCatalogServices>;
  safServicesWithSelection$: Observable<PaxServiceCategoryPerFragmentWithSelections>;
  totalPrice$: Observable<Amount>;

  subscriptions: Subscription = new Subscription();
  isSafSelected$: Observable<boolean>;
  cartBeforeSafUpdated: Subject<CartOrOrder> = new Subject<CartOrOrder>();
  openModal = false;
  safModalFragment$: Observable<CmsArticle>;

  constructor(
    private store$: Store<AppState & CommonBookingState>,
    private bookingQueueService: BookingQueueService,
    private toasterService: ToasterService,
    private sentryLogger: SentryLogger,
    private gtmService: GtmService,
    private fragmentService: FragmentService
  ) {}

  ngOnInit(): void {
    this.safService$ = this.store$.pipe(servicesForCategory(Category.SAF));

    this.totalPrice$ = this.safService$.pipe(map((safServices) => this.getTotalPrice(safServices)));
    this.subscriptions.add(this.totalPrice$.subscribe());

    this.subscriptions.add(
      combineLatest([this.cartOrOrder$, this.cartBeforeSafUpdated])
        .pipe(
          map(([order, oldOrder]) => changedServices(oldOrder, order, Category.SAF, GtmPurchaseFlow.BOOKING)),
          distinctUntilChanged(isDeepEqual),
          filter(({ addedProducts, removedProducts }) => addedProducts.length > 0 || removedProducts.length > 0)
        )
        .subscribe(({ addedProducts, removedProducts }) => {
          if (addedProducts.length) {
            this.gtmService.ecommerceEventGA4(GtmEvent.ADD_TO_CART, addedProducts);
          }

          if (removedProducts.length) {
            this.gtmService.ecommerceEventGA4(GtmEvent.REMOVE_FROM_CART, removedProducts);
          }
        })
    );

    const selections$ = this.safService$.pipe(
      switchMap((safServices) =>
        combineLatest(
          Object.keys(safServices).map((fragmentId) =>
            this.store$.pipe(
              serviceSelectionsForFragment<TravelerService[]>(fragmentId, Category.SAF),
              map((selections) =>
                valuesOf(selections)
                  .reduce((all, services) => all.concat(...services), [])
                  .filter((service) => service.quantity > 0)
              ),
              finShare()
            )
          )
        )
      ),
      map((selections) => selections.flat())
    );

    this.isSafSelected$ = selections$.pipe(
      map((services) => services.filter((service) => service?.variant === 'SAFL').length > 0)
    );

    this.safServicesWithSelection$ = combineLatest([this.isSafSelected$, this.safService$]).pipe(
      map(([isSafSelected, safServices]) => {
        const selectedQuantity = isSafSelected ? 1 : 0;
        const selection = { selected: isSafSelected, selectedQuantity };

        const serviceCatalogServiceWithSelection = Object.entries(Object.values(safServices ?? {})[0] ?? {})?.reduce(
          (innerAcc, [innerFragment, innerServices]) => {
            innerAcc[innerFragment] = innerServices.map((scs) => ({ ...scs, ...selection }));
            return innerAcc;
          },
          {}
        );

        this.serviceCatalogWithSelection = Object.values(serviceCatalogServiceWithSelection)[0][0];

        return serviceCatalogServiceWithSelection ?? {};
      })
    );

    this.subscriptions.add(this.safServicesWithSelection$.subscribe());

    this.subscriptions.add(
      this.servicesReady$.subscribe((serviceReady) => {
        if (serviceReady) {
          this.safIsLoading$.next(false);
        }
      })
    );

    this.safModalFragment$ = this.fragmentService.getFragment<CmsArticle>(
      'fragments.services.productInfo.saf.contributeUrl'
    );
  }

  getTotalPrice(safServices: ServiceCatalogServices): Amount {
    return Object.values(safServices)
      .flatMap((entries) => Object.values(entries).flat())
      .filter((item) => item.category === Category.SAF && item.subCategory === SubCategory.SAF_LOW)
      .reduce(
        (acc, item) => ({
          amount: (parseFloat(acc.amount) + parseFloat(item.totalPrice.amount)).toString(),
          currencyCode: item.totalPrice.currencyCode,
        }),
        { amount: '0', currencyCode: '' }
      );
  }

  updateSelectionsForFragment(serviceSelection: ServiceSelectionChange): void {
    this.subscriptions.add(
      combineLatest([this.safService$, this.isSafSelected$, this.cartOrOrder$])
        .pipe(
          filter(([_services, isSafSelected, _cartOrOrder]) => isPresent(serviceSelection.selection) !== isSafSelected),
          map(([services, , cartOrOrder]) =>
            Object.entries(services).map(([fragmentId, travelerServices]) => ({
              fragmentId,
              category: serviceSelection.category,
              selections: Object.keys(travelerServices).reduce<Record<string, TravelerService[]>>((acc, travelerId) => {
                acc[travelerId] = serviceSelection.selection ? [serviceSelection.selection] : [];
                return acc;
              }, {}),
              cartOrOrder,
            }))
          ),
          take(1)
        )
        .subscribe((selections) => {
          selections.forEach((selection) => {
            try {
              this.store$.dispatch(ServicesActions.setSelectionsForFragment(selection));
            } catch (error) {
              this.sentryLogger.error('Selecting sustainable aviation fuel(SAF) failed', {
                error: mapErrorForSentry(error),
              });
            }
          });

          this.safIsLoading$.next(true);

          const flights: FinnairItineraryItemFlight[] = getAllFlightsFromCartOrOrder(selections[0].cartOrOrder);
          const [requests, messages] = getServiceData(
            this.store$,
            serviceSelection.category,
            selections[0].cartOrOrder.bounds,
            flights
          );

          this.bookingQueueService.queueServicesUpdate(requests, {
            error: () => {
              this.toasterService.addMessageToQueue(messages.error);
            },
            complete: () => {
              this.cartBeforeSafUpdated.next(selections[0].cartOrOrder);
            },
          });
        })
    );
  }
}
